<template>
  <BPCard
    elementId="settlements"
    :headerConfig="headerConfig"
    :contractMode.sync="contractMode"
    :disabled="loading"
    hideNonTract
  >
    <v-row class="mr-2" dense align="center">
      <v-col cols="auto" class="mb-n6">
        <PayPeriodSelector @category-changed="currentModuleSetter"/>
      </v-col>
      <v-col>
        <Icon
        icon="mdi-plus"
        iconColor="primary"
        elementId="new-export-batch-button"
        :small="false"
        :tooltipText="$t('createExportBatch')"
        @icon-clicked="openExportBatchDialog()"
        :disabled="loading || !userAssignedClaim(UserClaims.ExportManager)"/>
      </v-col>
      <v-col cols="auto">
        <Icon
        icon="mdi-refresh"
        iconColor="primary"
        dataTestId="settlement-refresh-button"
        :small="false"
        :tooltipText="$t('refresh')"
        :disabled="loading"
        @icon-clicked="fetchExportBatches(dateConfiguration.payPeriod.payPeriodId)"/>
        <SettlementActionMenu
        v-if="!hideSettlementData"
        :isByProducts="isByProducts"
        :isExporter="userAssignedClaim(UserClaims.ExportManager)"
        :disabled="loading"
        @export-button-pressed="exportButtonPressed"
        @open-ticket-pdf-print="openTicketPdfDialog()"
        @open-integrations-dialog="openIntegrationsDialog()"
        @open-rate-verification-dialog="openRateVerificationDialog()"
        @rebuild-archive-data="backpopulateExportBatch"
        :containerClass="settlementActionContainerClass"
        />
      </v-col>
      <v-col cols="12" v-if="hideSettlementData">
        <v-row class="my-12 pa-12" justify="center" align="center">
          <span class="headline black--text">
            {{ $t('exportInProgressPleaseRefresh') }}
          </span>
        </v-row>
      </v-col>
      <v-col cols="12" v-else class="pa-0">
        <AccountsPayableModule v-if="!isByProducts && currentCategory === AccountingCategory.Payable"/>
        <BPAccountsPayableModule v-if="isByProducts && currentCategory === AccountingCategory.Payable"/>
        <AccountsReceivableModule v-if="!isByProducts && currentCategory === AccountingCategory.Receivable" />
        <BPAccountsReceivableModule v-if="isByProducts && currentCategory === AccountingCategory.Receivable" />
        <AccrualsModule v-if="!isByProducts && currentCategory === AccountingCategory.Accrual"/>
        <BPAccrualsModule v-if="isByProducts && currentCategory === AccountingCategory.Accrual"/>
      </v-col>
    </v-row>

    <Dialog
    :stateId="dialogId"
    @close="resetDialogs"
    >
      <SettlementTicketsPDFDialog
      v-if="ticketPDFDialog"
      @close="resetDialogs"/>
      <ExportBatchDialog v-if="exportBatchDialog"
      @close="resetDialogs"/>
      <IntegrationsDialog
      v-if="integrationsDialog"
      :dateConfiguration="dateConfiguration"/>
      <RateVerificationDialog
      v-if="rateVerificationDialog"
      @close="resetDialogs"
      @refresh-batch="refreshModuleAndClearStaleData()"
      :contractMode="contractMode"/>
    </Dialog>
    <v-dialog v-model="exportDialog" :width="showSettlementMessage ? 600 : 400">
      <v-card v-if="exportDialog">
        <v-card-title class="primary white--text headline" style="word-break: break-word">
          {{ confirmationTitle }}
        </v-card-title>
        <v-card-text class="mt-6">
          <p class="subtitle-1 black--text font-weight-medium" style="white-space: pre-line">
            {{ confirmationSubtitle }}
          </p>
          <v-row v-if="showSettlementMessage" class="mt-2 px-2">
            <v-card outlined width="100%">
              <v-card-title class="subtitle-1 pb-2">
                <span>{{ $t('settlementMessage') }}</span>
                <Icon
                margin="ml-2"
                icon="mdi-pencil"
                :tooltipText="$t('editSettlementMessage')"
                @icon-clicked="openSettlementMessageDialog"/>
              </v-card-title>
              <v-card-text class="pt-0">
                <span class="body-2 text-pre-wrap">{{ settlementMessage || $t('none') }}</span>
              </v-card-text>
            </v-card>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-container class="pa-2">
            <v-row dense>
              <v-col>
                <v-btn class="ml-n4" text outlined color="black" @click="resetDialogs">{{ $t('cancel') }}</v-btn>
              </v-col>
              <v-col cols="auto">
                <v-btn class="primary mr-n4" @click="exportPayPeriod">{{ $t('confirm') }}</v-btn>
              </v-col>
            </v-row>
          </v-container>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="settlementMessageDialog" width="500" persistent>
      <v-card v-if="settlementMessageDialog">
        <v-card-title class="primary headline white--text">
          <span>{{ $t('editSettlementMessage') }}</span>
          <v-spacer/>
          <Icon
          icon="mdi-close"
          iconColor="white"
          :small="false"
          large
          :tooltipText="$t('cancel')"
          @icon-clicked="closeSettlementMessageDialog"/>
        </v-card-title>
        <v-card-text class="mt-6">
          <FormWrapper
          @submit="saveSettlementMessage"
          :buttonText="$t('done')">
            <v-textarea
            v-model="settlementMessageDraft"
            :label="$t('settlementMessage')"
            :hint="$t('settlementMessageHint')"
            outlined
            persistent-hint
            counter
            maxlength="512"
            rows="4"
            auto-grow
            autofocus/>
          </FormWrapper>
        </v-card-text>
      </v-card>
    </v-dialog>
  </BPCard>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { SettlementKeys, SettlementSubKeys } from '@/components/settlements/SettlementKeys.js'
import { LocalStorageKeys } from '@/utils/LocalStorageActor'
import { utcToLocalDate } from '@/utils/DateFormatter.js'
import { PayPeriodStatus, ContractMode, AccountingCategory, UserClaims, SettlementArchiveStatus, SettlementEmailMessageMode } from '@/utils/Enumerations.js'
import { userAssignedClaim } from '@/utils/ClaimUtility.js'
import { kebabCase } from 'lodash'
import { sanitizeSettlementMessage, containsUrl } from '@/utils/SettlementMessageSanitizer.js'

export default {
  name: 'Settlements',

  components: {
    PayPeriodSelector: () => import('@/components/settlements/PayPeriodSelector.vue'),
    AccountsPayableModule: () => import('@/components/settlements/accounts-payable-module/AccountsPayableModule.vue'),
    BPAccountsPayableModule: () => import('@/components/settlements/accounts-payable-module/BPAccountsPayableModule.vue'),
    AccountsReceivableModule: () => import('@/components/settlements/accounts-receivable-module/AccountsReceivableModule.vue'),
    BPAccountsReceivableModule: () => import('@/components/settlements/accounts-receivable-module/BPAccountsReceivableModule.vue'),
    AccrualsModule: () => import('@/components/settlements/accruals-module/AccrualsModule.vue'),
    BPAccrualsModule: () => import('@/components/settlements/accruals-module/BPAccrualsModule.vue'),
    SettlementActionMenu: () => import('@/components/settlements/SettlementActionMenu.vue'),
    SettlementTicketsPDFDialog: () => import('@/components/settlements/SettlementTicketsPDFDialog.vue'),
    IntegrationsDialog: () => import('@/components/settlements/business-integrations/IntegrationsDialog.vue'),
    Icon: () => import('@/components/helper/Icon.vue'),
    BPCard: () => import('@/components/core/BPCard.vue'),
    Dialog: () => import('@/components/Dialog.vue'),
    ExportBatchDialog: () => import('@/components/settlements/export-batch-form/ExportBatchDialog.vue'),
    RateVerificationDialog: () => import('@/components/settlements/rate-verification/RateVerificationDialog.vue'),
    FormWrapper: () => import('@/components/core/FormWrapper.vue')
  },

  data: () => ({
    currentCategory: AccountingCategory.enums[0],
    dialogId: 'settlements-dialog',
    exportDialog: false,
    exportBatchDialog: false,
    correctionsDialog: false,
    integrationsDialog: false,
    readyForPaymentTickets: [],
    postedTickets: [],
    ticketPDFDialog: false,
    contractMode: null,
    rateVerificationDialog: false,
    pollingHandle: undefined,
    pollCount: 0,
    AccountingCategory,
    UserClaims,
    settlementMessage: '',
    settlementMessageDialog: false,
    settlementMessageDraft: ''
  }),

  watch: {
    currentCategory (val) {
      this.currentModuleSetter(val)
    },

    currentSubModule: {
      handler (val, old) {
        if (!val && !old) return
        if (val === undefined && !this.hideSettlementData) {
          this.$nextTick(_ => {
            this.refreshModuleData()
          })
        }
      },
      deep: true
    },

    dateConfiguration: {
      handler (val, oldVal) {
        const newSelectionHasData =
          (oldVal.exportBatch?.settlementArchiveStatus === SettlementArchiveStatus.Pending.value || oldVal.exportBatch?.settlementArchiveStatus === SettlementArchiveStatus.Upgrade.value) && !this.hideSettlementData

        if (oldVal?.exportBatch === undefined || newSelectionHasData) {
          this.refreshModuleData(false)
        } else if (val?.exportBatch?.settlementArchiveStatus === SettlementArchiveStatus.None.value) {
          this.backpopulateExportBatch(val.exportBatch)
        }
        if (val?.exportBatch?.exportBatchId !== oldVal?.exportBatch?.exportBatchId) this.pollData()
      },
      deep: true
    },

    contractMode (cm) {
      localStorage.setItem(LocalStorageKeys.SETTLEMENTS_CONTRACT_MODE, cm.value)
      this.currentModuleSetter(this.currentCategory)
    }
  },
  created () {
    const savedContractMode = parseInt(localStorage.getItem(LocalStorageKeys.SETTLEMENTS_CONTRACT_MODE)) || undefined
    this.contractMode = (savedContractMode && ContractMode.forInt(savedContractMode)) ?? ContractMode.Logs
  },

  beforeDestroy () {
    this.resetModules()
    clearInterval(this.polling)
  },

  mounted () {
    if (!this.currentSubModule) {
      this.$nextTick(_ => {
        this.refreshModuleData()
      })
    }
  },

  computed: {
    ...mapGetters('settlements', ['moduleNames', 'dateConfiguration', 'currentSubModule', 'loading']),
    ...mapGetters('user', ['userClaims', 'companyInfo']),

    showSettlementMessage () {
      return this.companyInfo?.settlementEmailMessageMode === SettlementEmailMessageMode.Default.value
    },
    subtitle () {
      const batch = this.dateConfiguration.exportBatch
      return this.dateConfiguration?.exportBatch?.exportedBy
        ? this.$t('batchExportedByNOnDate', { batch: batch.label, n: batch.exportedBy, date: utcToLocalDate(batch.exportDate, 'L - LT') })
        : this.$t('noExportBatchSelected')
    },

    isByProducts () {
      return this.contractMode?.value === 1
    },

    headerConfig () {
      switch (this.contractMode?.value) {
        case 0: return { title: this.$t('settlements') }
        case 1: return { title: this.$t('byProductSettlements') }
      }

      return null
    },

    confirmationTitle () {
      const { payPeriod } = this.dateConfiguration
      if (payPeriod) {
        return this.$t('exportPayPeriod')
      } else {
        return ''
      }
    },

    confirmationSubtitle () {
      const { payPeriod } = this.dateConfiguration
      if (!payPeriod) return ''

      return (this.readyForPaymentTickets === 0)
        ? this.$t('exportSelectedPayPeriodAndFinalize')
        : `${this.$t('exportSelectedPayPeriodAndFinalize')} \n\n Tickets Ready For Payment: ${this.readyForPaymentTickets}* \n Tickets Posted: ${this.postedTickets} \n\n *Exporting this pay period will roll the tickets ready for payment to the next pay period.`
    },

    correctionsButtonDisabled () {
      const { payPeriod } = this.dateConfiguration

      if (payPeriod === undefined || payPeriod === null) {
        return true
      }

      return payPeriod.corrections <= 0
    },

    hideSettlementData () {
      const exportBatch = this.dateConfiguration.exportBatch
      return exportBatch?.settlementArchiveStatus === SettlementArchiveStatus.Pending.value ||
        exportBatch?.settlementArchiveStatus === SettlementArchiveStatus.None.value ||
        exportBatch?.settlementArchiveStatus === SettlementArchiveStatus.Upgrade.value
    },

    settlementActionContainerClass () {
      return `${kebabCase(this.contractMode.label)} ${kebabCase(this.currentCategory.label)}`
    }
  },

  methods: {
    ...mapActions('settlements', ['refreshModuleData', 'setCurrentModule', 'resetModules', 'clearStaleSubModuleData', 'backpopulateExportBatchArchive']),
    ...mapActions('dialog', ['openOrUpdateDialog', 'closeDialogsAtOrAbove']),
    ...mapActions('pay-period', ['getPayPeriodWithId', 'exportPeriod', 'fetchPayPeriods']),
    ...mapActions('export-batch', ['fetchExportBatches']),
    userAssignedClaim,

    statusFromInt: (x) => PayPeriodStatus.fromInt(x),
    moduleSelected (moduleName) {
      this.currentModule = this.moduleNames.findIndex(mn => mn === moduleName)
    },

    resetDialogs (refresh = false) {
      this.exportDialog = false
      this.exportBatchDialog = false
      this.integrationsDialog = false
      this.ticketPDFDialog = false
      this.rateVerificationDialog = false
      this.readyForPaymentTickets = []
      this.postedTickets = []
      this.settlementMessage = ''
      this.settlementMessageDialog = false
      this.settlementMessageDraft = ''
      this.closeDialogsAtOrAbove(this.dialogId)

      if (refresh) {
        this.fetchExportBatches(this.dateConfiguration.payPeriod.payPeriodId)
        this.$nextTick(_ => {
          this.clearStaleSubModuleData({ isByproduct: this.isByProducts, allData: true })
        })
      }
    },

    openExportBatchDialog () {
      this.exportBatchDialog = true
      this.openOrUpdateDialog({ id: this.dialogId, width: '80%' })
    },

    openTicketPdfDialog () {
      this.ticketPDFDialog = true
      this.openOrUpdateDialog({ id: this.dialogId, width: '600' })
    },

    openIntegrationsDialog () {
      this.integrationsDialog = true
      this.openOrUpdateDialog({ id: this.dialogId, width: '80%' })
    },

    openRateVerificationDialog () {
      this.rateVerificationDialog = true
      this.openOrUpdateDialog({ id: this.dialogId, width: '60%' })
    },

    async exportButtonPressed () {
      const { payPeriod, fiscalYear } = this.dateConfiguration

      const res = await this.getPayPeriodWithId({
        payPeriodId: payPeriod.payPeriodId,
        fiscalYearId: fiscalYear.fiscalYearId
      })

      this.readyForPaymentTickets = res.readyForPayment
      this.postedTickets = res.posted
      this.settlementMessage = this.showSettlementMessage ? (this.companyInfo?.defaultSettlementMessage ?? '') : ''
      this.exportDialog = true
    },

    openSettlementMessageDialog () {
      this.settlementMessageDraft = this.settlementMessage
      this.settlementMessageDialog = true
    },

    closeSettlementMessageDialog () {
      this.settlementMessageDialog = false
      this.settlementMessageDraft = ''
    },

    saveSettlementMessage () {
      const sanitized = sanitizeSettlementMessage(this.settlementMessageDraft)
      if (containsUrl(sanitized)) {
        this.setSnackError(this.$t('settlementMessageNoUrls'))
        return
      }
      this.settlementMessage = sanitized
      this.closeSettlementMessageDialog()
    },

    async exportPayPeriod () {
      const confirmation = confirm('Are you sure you want to export the tickets for this pay period? There is no way to reverse this action.')
      if (confirmation) {
        const { payPeriod, fiscalYear } = this.dateConfiguration
        const requestObj = {
          payPeriod: JSON.parse(JSON.stringify(payPeriod)),
          settlementMessage: this.showSettlementMessage ? sanitizeSettlementMessage(this.settlementMessage) : ''
        }
        await this.exportPeriod(requestObj)
        await this.fetchPayPeriods(fiscalYear.fiscalYearId)
      }

      this.exportDialog = false
      this.resetDialogs({ refresh: true })
    },

    currentModuleSetter (val) {
      switch (val) {
        case AccountingCategory.Payable:
          this.setCurrentModule({
            moduleKey: SettlementKeys.AccountsPayable,
            subModuleKey: this.isByProducts ? SettlementSubKeys.AP.ByproductSettlementsByPayee : SettlementSubKeys.AP.ProductionSummary
          })
          break
        case AccountingCategory.Receivable:
          this.setCurrentModule({
            moduleKey: SettlementKeys.AccountsReceivable,
            subModuleKey: this.isByProducts ? SettlementSubKeys.AR.BPReceivableRegister : SettlementSubKeys.AR.ReceivableRegister
          })
          break
        case AccountingCategory.Accrual:
          this.setCurrentModule({
            moduleKey: SettlementKeys.Accruals,
            subModuleKey: this.isByProducts ? SettlementSubKeys.AC.BPRegister : SettlementSubKeys.AC.Register
          })
          break
      }
      if (this.currentCategory !== val) this.currentCategory = val
    },

    async refreshModuleAndClearStaleData () {
      await this.refreshModuleData(false)
      this.clearStaleSubModuleData({ isByproduct: this.isByProducts, allData: true })
    },

    async backpopulateExportBatch (exportBatch) {
      await this.backpopulateExportBatchArchive(exportBatch)
      await this.fetchExportBatches(exportBatch.payPeriodId)
    },

    pollData () {
      const interval = 2500
      const maxPoll = 5
      this.pollCount = 0
      if (!this.hideSettlementData) return
      this.pollingHandle = setInterval(async () => {
        if (this.pollCount >= maxPoll || !this.hideSettlementData) {
          clearInterval(this.pollingHandle)
        } else {
          await this.fetchExportBatches(this.dateConfiguration.payPeriod.payPeriodId)
        }
        this.pollCount++
      }, interval)
    }
  }
}
</script>
